import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PreviewOutlinedIcon from "@mui/icons-material/PreviewOutlined";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useAccount } from "../../../../context";
import { getCrmDomain } from "../../../../services/api";
import {
  DEFAULT_FIELD_LABELS,
  DEFAULT_QUALITY_FIELD_LABELS,
} from "../constants/lead-visible-fields";
import {
  CampaignVisibleFieldsConfig,
  LeadContact,
} from "../types/lead-contacts.types";
import { buildCopyLines, copyLinesToText } from "../utils/buildCopyLines";
import { getExtraFields } from "../utils/contactOthers";

interface LeadDetailsModalProps {
  open: boolean;
  contact: LeadContact | null;
  onClose: () => void;
  onCopy: (contact: LeadContact) => void;
  /** Config da campanha (campos visíveis, rótulos, extras). Se undefined, mostra todos os padrão com rótulos padrão. */
  visibleFieldsConfig?: CampaignVisibleFieldsConfig | null;
}

const LeadDetailsModal: React.FC<LeadDetailsModalProps> = ({
  open,
  contact,
  onClose,
  onCopy,
  visibleFieldsConfig,
}) => {
  const [isCopied, setIsCopied] = useState(false);
  const [crmDomain, setCrmDomain] = useState<string | null>(null);
  const { account } = useAccount();

  useEffect(() => {
    if (!open || !account?.company_id) return;
    getCrmDomain(account.company_id)
      .then((res) => {
        if (res.status === "success" && res.data?.domain)
          setCrmDomain(res.data.domain);
        else setCrmDomain(null);
      })
      .catch(() => setCrmDomain(null));
  }, [open, account?.company_id]);

  const extras = useMemo(
    () => (contact ? getExtraFields(contact.others) : {}),
    [contact],
  );

  /** URL do lead no CRM. Em repescagem usa recap_campaign_id; senão campaign_id. */
  const crmLeadUrl = useMemo(() => {
    if (!contact || !crmDomain) return null;
    const campaignId =
      contact.is_from_recap && contact.recap_campaign_id != null
        ? contact.recap_campaign_id
        : contact.campaign_id;
    if (campaignId == null) return null;
    const base = crmDomain.replace(/\/$/, "");
    const name = (contact.valid_name || contact.name || "").trim();
    const email = (contact.email || "").trim();
    const phone = (contact.telephone || contact.valid_telephone || "").replace(
      /\D/g,
      "",
    );
    const params = new URLSearchParams({
      "search-name": name,
      "search-email": email,
      "search-phone": phone,
    });
    return `${base}/leads/${campaignId}/?${params.toString()}`;
  }, [crmDomain, contact]);

  const formattedCopy = useMemo(() => {
    if (!contact) return "";
    const lines = buildCopyLines(contact, extras, visibleFieldsConfig);
    return copyLinesToText(lines);
  }, [contact, extras, visibleFieldsConfig]);

  const shouldShow = useMemo(() => {
    if (!visibleFieldsConfig) return () => true;
    if (visibleFieldsConfig.visible_fields.length === 0) return () => false;
    const set = new Set(visibleFieldsConfig.visible_fields);
    return (fieldId: string) => set.has(fieldId);
  }, [visibleFieldsConfig]);

  const getLabel = (fieldId: string) => {
    const custom = visibleFieldsConfig?.field_labels?.[fieldId];
    if (custom != null && String(custom).trim() !== "")
      return String(custom).trim();
    return (
      DEFAULT_FIELD_LABELS[fieldId as keyof typeof DEFAULT_FIELD_LABELS] ??
      DEFAULT_QUALITY_FIELD_LABELS[
        fieldId as keyof typeof DEFAULT_QUALITY_FIELD_LABELS
      ] ??
      fieldId
    );
  };

  const visibleExtras = useMemo(() => {
    const list = visibleFieldsConfig?.extra_fields ?? [];
    return list.filter((e) => extras[e.key]);
  }, [visibleFieldsConfig?.extra_fields, extras]);

  // Versão renderizada para o preview (labels em negrito, sem asteriscos)
  const formattedCopyDisplayLines = useMemo(() => {
    if (!contact) return [];
    return formattedCopy.split("\n").map((line, index) => {
      const match = line.match(/^\*([^*]+):\*\s*(.*)$/);
      if (match) {
        return { key: index, label: match[1], value: match[2] };
      }
      return { key: index, label: "", value: line };
    });
  }, [contact, formattedCopy]);

  const handleCopy = () => {
    if (contact) {
      onCopy(contact);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    }
  };

  const getStatusColor = (status: string) => {
    const s = (status || "").toLowerCase();
    switch (s) {
      case "novo":
        return "#008069";
      case "em andamento":
        return "#1976d2";
      case "concluído":
        return "#2e7d32";
      case "cancelado":
        return "#d32f2f";
      case "sem interesse":
        return "#f57c00";
      case "solicita exclusão do cadastro":
        return "#c62828";
      case "interessado":
        return "#4caf50";
      case "agendamento":
        return "#ff9800";
      default:
        return "#667781";
    }
  };

  const displayStatus = contact?.is_quality_lead
    ? (contact?.store_status ?? contact?.status)
    : contact?.status;

  const dateTimeFormatter = new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  const getDisplayValue = <K extends keyof LeadContact>(
    fieldId: K,
    defaultValue: string = "NÃO INFORMADO",
    formatDate: boolean = false,
  ): string => {
    const rawValue = contact?.[fieldId];

    if (rawValue == null || rawValue === "") {
      return defaultValue;
    }

    if (formatDate) {
      const date = new Date(rawValue as string);

      if (isNaN(date.getTime())) {
        return String(rawValue);
      }

      return dateTimeFormatter.format(date);
    }

    return String(rawValue);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      slotProps={{
        paper: {
          sx: {
            borderRadius: "12px",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
            maxHeight: "90vh",
            "@media (max-width: 600px)": {
              margin: "8px",
              maxWidth: "calc(100% - 16px)",
              maxHeight: "calc(100vh - 16px)",
              height: "auto", // Altura automática em mobile
              minHeight: "400px", // Altura mínima para garantir usabilidade
            },
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pr: 1,
          pb: 2,
          background: "linear-gradient(135deg, #008069 0%, #006d5b 100%)",
          color: "white",
          gap: 1,
          "@media (max-width: 600px)": {
            pr: 0.5,
            pb: 1.5,
            flexWrap: "wrap",
          },
        }}
      >
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}
        >
          <Typography
            variant="h6"
            sx={{
              fontSize: "18px",
              fontWeight: 700,
              "@media (max-width: 600px)": { fontSize: "16px" },
            }}
          >
            Detalhes do Lead
          </Typography>
          {crmLeadUrl && (
            <Tooltip title="Abrir lead no CRM em nova aba">
              <Button
                component="a"
                href={crmLeadUrl}
                target="_blank"
                rel="noopener noreferrer"
                size="small"
                startIcon={<OpenInNewIcon sx={{ fontSize: "18px" }} />}
                sx={{
                  color: "white",
                  borderColor: "rgba(255,255,255,0.8)",
                  borderRadius: "6px",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "12px",
                  py: 0.5,
                  px: 1,
                  minWidth: "auto",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.15)",
                    borderColor: "white",
                  },
                  "@media (max-width: 600px)": {
                    fontSize: "11px",
                    py: 0.4,
                    px: 0.75,
                    "& .MuiButton-startIcon": { marginRight: 0.25 },
                  },
                }}
                variant="outlined"
              >
                <Box
                  component="span"
                  sx={{ "@media (max-width: 400px)": { display: "none" } }}
                >
                  Ver no CRM
                </Box>
                <Box
                  component="span"
                  sx={{
                    display: "none",
                    "@media (max-width: 400px)": { display: "inline" },
                  }}
                >
                  CRM
                </Box>
              </Button>
            </Tooltip>
          )}
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            flexShrink: 0,
          }}
        >
          <IconButton
            onClick={onClose}
            aria-label="Fechar"
            sx={{
              color: "white",
              "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" },
              "@media (max-width: 600px)": { padding: "4px" },
            }}
          >
            <CloseIcon
              sx={{ "@media (max-width: 600px)": { fontSize: "20px" } }}
            />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          p: 2,
          maxHeight: "calc(90vh - 80px)", // Altura máxima considerando header
          overflowY: "auto", // Scroll no body do modal
          "@media (max-width: 600px)": {
            p: 1.5,
            maxHeight: "calc(100vh - 120px)", // Altura máxima considerando header
          },
        }}
      >
        {contact && (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "stretch",
                gap: 2,
                minHeight: "fit-content",
                "@media (max-width: 600px)": {
                  flexDirection: "column",
                  alignItems: "stretch",
                  gap: 1.5,
                },
              }}
            >
              {/* LADO ESQUERDO: Informações do Lead */}
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  minWidth: 0,
                  justifyContent: "space-between",
                  "@media (max-width: 600px)": {
                    flex: "0 0 auto",
                    gap: 1.5,
                    justifyContent: "flex-start",
                  },
                }}
              >
                {/* Header com informações principais */}
                {(shouldShow("client_name") ||
                  shouldShow("lead_id") ||
                  shouldShow("status") ||
                  shouldShow("fixed")) && (
                  <Card
                    sx={{
                      background:
                        "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
                      border: "1px solid #008069",
                      borderRadius: "8px",
                      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                      flex: "0 0 auto",
                    }}
                  >
                    <CardContent sx={{ p: 1.5 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          justifyContent: "space-between",
                          mb: 1.5,
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          {(shouldShow("name") || shouldShow("valid_name")) && (
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 700,
                                color: "#111b21",
                                mb: 0.25,
                                fontSize: "16px",
                                overflowWrap: "break-word",
                                wordBreak: "break-word",
                              }}
                            >
                              {getDisplayValue("name") ||
                                getDisplayValue("valid_name")}
                            </Typography>
                          )}

                          {shouldShow("lead_id") && (
                            <Typography
                              variant="body2"
                              sx={{
                                color: "#667781",
                                fontSize: "12px",
                                overflowWrap: "break-word",
                                wordBreak: "break-word",
                              }}
                            >
                              ID: #{getDisplayValue("lead_id")}
                            </Typography>
                          )}
                        </Box>

                        {/* COLUNA DA DIREITA */}
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-end",
                            gap: 1,
                          }}
                        >
                          {shouldShow("status") && (
                            <Chip
                              label={displayStatus}
                              sx={{
                                backgroundColor: getStatusColor(
                                  displayStatus ?? "",
                                ),
                                color: "white",
                                fontWeight: 600,
                                fontSize: "11px",
                                height: "24px",
                              }}
                            />
                          )}
                        </Box>
                      </Box>
                      {shouldShow("fixed") && (
                        <Alert
                          severity="warning"
                          sx={{
                            mb: 1.5,
                            px: 1,
                            py: 0.5,
                            backgroundColor: "#fff3cd",
                            border: "1px solid #ffeaa7",
                            borderRadius: "6px",
                            "& .MuiAlert-icon": { color: "#856404" },
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: "#856404",
                              fontSize: "12px",
                            }}
                          >
                            Este lead foi alterado e requer atenção especial
                          </Typography>
                        </Alert>
                      )}

                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: 1.5,
                          "@media (max-width: 600px)": {
                            gridTemplateColumns: "1fr",
                            gap: 1,
                          },
                        }}
                      >
                        {shouldShow("campaign_name") && (
                          <Box
                            sx={{
                              gridColumn: "span 2",
                              "@media (max-width: 600px)": {
                                gridColumn: "span 1",
                              },
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#667781",
                                fontWeight: 600,
                                fontSize: "10px",
                              }}
                            >
                              {getLabel("campaign_name").toUpperCase()}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                color: "#111b21",
                                fontSize: "13px",
                                overflowWrap: "break-word",
                                wordBreak: "break-word",
                              }}
                            >
                              {getDisplayValue("campaign_name")}
                            </Typography>
                          </Box>
                        )}
                        {shouldShow("telephone") && (
                          <Box>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#667781",
                                fontWeight: 600,
                                fontSize: "10px",
                              }}
                            >
                              {getLabel("telephone").toUpperCase()}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                color: "#111b21",
                                fontSize: "13px",
                              }}
                            >
                              {getDisplayValue("valid_telephone")}
                            </Typography>
                          </Box>
                        )}
                        {shouldShow("other_contacts") && (
                          <Box>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#667781",
                                fontWeight: 600,
                                fontSize: "10px",
                              }}
                            >
                              {getLabel("other_contacts").toUpperCase()}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                color: "#111b21",
                                fontSize: "13px",
                                overflowWrap: "break-word",
                                wordBreak: "break-word",
                              }}
                            >
                              {getDisplayValue("other_contacts")}
                            </Typography>
                          </Box>
                        )}
                        {shouldShow("email") && (
                          <Box>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#667781",
                                fontWeight: 600,
                                fontSize: "10px",
                              }}
                            >
                              {getLabel("email").toUpperCase()}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                color: "#111b21",
                                fontSize: "13px",
                                overflowWrap: "break-word",
                                wordBreak: "break-word",
                              }}
                            >
                              {getDisplayValue("email")}
                            </Typography>
                          </Box>
                        )}
                        {shouldShow("store") && (
                          <Box>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#667781",
                                fontWeight: 600,
                                fontSize: "10px",
                              }}
                            >
                              {getLabel("store").toUpperCase()}
                            </Typography>
                            <Chip
                              label={getDisplayValue("store", "NÃO INFORMADO")}
                              size="small"
                              variant="outlined"
                              sx={{
                                height: 22,
                                fontSize: "12px",
                                fontWeight: 600,
                                borderColor: "#008069",
                                color: "#008069",
                                backgroundColor: "rgba(0, 128, 105, 0.08)",
                                ml: 1,
                              }}
                            />
                          </Box>
                        )}
                        {visibleExtras.map(({ key, label }) => (
                          <Box key={key}>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#667781",
                                fontWeight: 600,
                                fontSize: "10px",
                              }}
                            >
                              {(label || key).toUpperCase()}
                            </Typography>
                            <Chip
                              label={getDisplayValue(
                                key as keyof LeadContact,
                                extras[key],
                              )}
                              size="small"
                              variant="outlined"
                              sx={{
                                height: 22,
                                fontSize: "12px",
                                fontWeight: 600,
                                borderColor: "#008069",
                                color: "#008069",
                                backgroundColor: "rgba(0, 128, 105, 0.08)",
                              }}
                            />
                          </Box>
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                )}

                {/* Informações de Atribuição */}
                {(shouldShow("attendant_name") ||
                  shouldShow("coordinator_name")) && (
                  <Card
                    sx={{
                      background:
                        "linear-gradient(135deg, #f0f7ff 0%, #e3f2fd 100%)",
                      border: "1px solid #008069",
                      borderRadius: "8px",
                      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                      flex: "0 0 auto",
                    }}
                  >
                    <CardContent sx={{ p: 1.5 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 1,
                        }}
                      >
                        <GroupsOutlinedIcon
                          sx={{ fontSize: "16px", color: "#008069" }}
                        />
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 700,
                            color: "#111b21",
                            fontSize: "13px",
                          }}
                        >
                          Responsáveis pelo Lead
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: 1,
                        }}
                      >
                        {shouldShow("attendant_name") && (
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                color: "#111b21",
                                fontSize: "13px",
                                overflowWrap: "break-word",
                                wordBreak: "break-word",
                              }}
                            >
                              {getDisplayValue("attendant_name")}
                            </Typography>
                          </Box>
                        )}
                        {shouldShow("coordinator_name") && (
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                color: "#111b21",
                                fontSize: "13px",
                                overflowWrap: "break-word",
                                wordBreak: "break-word",
                              }}
                            >
                              {getDisplayValue("coordinator_name")}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                )}

                {/* Informações do veículo */}
                {(shouldShow("vehicle") ||
                  shouldShow("vehicle_exchange") ||
                  shouldShow("vehicle_financing") ||
                  shouldShow("vehicle_estimated_purchase")) && (
                  <Card
                    sx={{
                      border: "1px solid #008069",
                      borderRadius: "8px",
                      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                      flex: "1 1 auto",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <CardContent
                      sx={{ p: 1.5, display: "flex", flexDirection: "column" }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 1.5,
                        }}
                      >
                        <DirectionsCarFilledOutlinedIcon
                          sx={{ fontSize: "18px", color: "#008069" }}
                        />
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 700,
                            color: "#111b21",
                            fontSize: "14px",
                          }}
                        >
                          Informações do Veículo
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: 1.5,
                        }}
                      >
                        {shouldShow("vehicle") && (
                          <Box>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#667781",
                                fontWeight: 600,
                                fontSize: "10px",
                              }}
                            >
                              {getLabel("vehicle").toUpperCase()}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                color: "#111b21",
                                fontSize: "13px",
                                overflowWrap: "break-word",
                                wordBreak: "break-word",
                              }}
                            >
                              {getDisplayValue("vehicle")}
                            </Typography>
                          </Box>
                        )}
                        {shouldShow("vehicle_exchange") && (
                          <Box>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#667781",
                                fontWeight: 600,
                                fontSize: "10px",
                              }}
                            >
                              {getLabel("vehicle_exchange").toUpperCase()}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                color: "#111b21",
                                fontSize: "13px",
                                overflowWrap: "break-word",
                                wordBreak: "break-word",
                              }}
                            >
                              {getDisplayValue("vehicle_exchange")}
                            </Typography>
                          </Box>
                        )}
                        {shouldShow("vehicle_financing") && (
                          <Box>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#667781",
                                fontWeight: 600,
                                fontSize: "10px",
                              }}
                            >
                              {getLabel("vehicle_financing").toUpperCase()}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                color: "#111b21",
                                fontSize: "13px",
                                overflowWrap: "break-word",
                                wordBreak: "break-word",
                              }}
                            >
                              {getDisplayValue("vehicle_financing")}
                            </Typography>
                          </Box>
                        )}
                        {shouldShow("vehicle_estimated_purchase") && (
                          <Box>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#667781",
                                fontWeight: 600,
                                fontSize: "10px",
                              }}
                            >
                              {getLabel(
                                "vehicle_estimated_purchase",
                              ).toUpperCase()}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                color: "#111b21",
                                fontSize: "13px",
                                overflowWrap: "break-word",
                                wordBreak: "break-word",
                              }}
                            >
                              {getDisplayValue("vehicle_estimated_purchase")}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                )}

                {/* Informações de Agendamento */}
                {(shouldShow("scheduled_date") ||
                  shouldShow("call_back_date")) && (
                  <Card
                    sx={{
                      border: "1px solid #008069",
                      borderRadius: "8px",
                      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                      flex: "0 0 auto",
                    }}
                  >
                    <CardContent sx={{ p: 1.5 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 1.5,
                        }}
                      >
                        <CalendarMonthOutlinedIcon
                          sx={{ fontSize: "18px", color: "#008069" }}
                        />
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 700,
                            color: "#111b21",
                            fontSize: "14px",
                          }}
                        >
                          Informações de Agendamento
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: 1.5,
                        }}
                      >
                        {shouldShow("scheduled_date") && (
                          <Box>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#667781",
                                fontWeight: 600,
                                fontSize: "10px",
                              }}
                            >
                              {getLabel("scheduled_date").toUpperCase()}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                color: "#111b21",
                                fontSize: "13px",
                                overflowWrap: "break-word",
                                wordBreak: "break-word",
                              }}
                            >
                              {getDisplayValue(
                                "scheduled_date",
                                "NÃO INFORMADO",
                                true,
                              )}
                            </Typography>
                          </Box>
                        )}
                        {shouldShow("call_back_date") && (
                          <Box>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#667781",
                                fontWeight: 600,
                                fontSize: "10px",
                              }}
                            >
                              {getLabel("call_back_date").toUpperCase()}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                color: "#111b21",
                                fontSize: "13px",
                                overflowWrap: "break-word",
                                wordBreak: "break-word",
                              }}
                            >
                              {getDisplayValue(
                                "call_back_date",
                                "NÃO INFORMADO",
                                true,
                              )}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                )}

                {/* Controle de Qualidade: todos os campos extras do lead qualidade */}
                {contact.is_quality_lead && (
                  <Card
                    sx={{
                      border: "1px solid #3949ab",
                      borderRadius: "8px",
                      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                      flex: "0 0 auto",
                    }}
                  >
                    <CardContent sx={{ p: 1.5 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 1.5,
                        }}
                      >
                        <InfoOutlinedIcon
                          sx={{ fontSize: "18px", color: "#3949ab" }}
                        />
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 700,
                            color: "#111b21",
                            fontSize: "14px",
                          }}
                        >
                          Controle de Qualidade
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: 1.5,
                          "@media (max-width: 600px)": {
                            gridTemplateColumns: "1fr",
                          },
                        }}
                      >
                        {shouldShow("store_contact") && (
                          <Box>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#667781",
                                fontWeight: 600,
                                fontSize: "10px",
                              }}
                            >
                              {getLabel("store_contact").toUpperCase()}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                color: "#111b21",
                                fontSize: "13px",
                                overflowWrap: "break-word",
                                wordBreak: "break-word",
                              }}
                            >
                              {getDisplayValue("store_contact")}
                            </Typography>
                          </Box>
                        )}
                        {shouldShow("store_visit") && (
                          <Box>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#667781",
                                fontWeight: 600,
                                fontSize: "10px",
                              }}
                            >
                              {getLabel("store_visit").toUpperCase()}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                color: "#111b21",
                                fontSize: "13px",
                                overflowWrap: "break-word",
                                wordBreak: "break-word",
                              }}
                            >
                              {getDisplayValue("store_visit")}
                            </Typography>
                          </Box>
                        )}
                        {shouldShow("store_buy") && (
                          <Box>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#667781",
                                fontWeight: 600,
                                fontSize: "10px",
                              }}
                            >
                              {getLabel("store_buy").toUpperCase()}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                color: "#111b21",
                                fontSize: "13px",
                                overflowWrap: "break-word",
                                wordBreak: "break-word",
                              }}
                            >
                              {getDisplayValue("store_buy")}
                            </Typography>
                          </Box>
                        )}
                        {shouldShow("store_buy_locale") && (
                          <Box>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#667781",
                                fontWeight: 600,
                                fontSize: "10px",
                              }}
                            >
                              {getLabel("store_buy_locale").toUpperCase()}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                color: "#111b21",
                                fontSize: "13px",
                                overflowWrap: "break-word",
                                wordBreak: "break-word",
                              }}
                            >
                              {getDisplayValue("store_buy_locale")}
                            </Typography>
                          </Box>
                        )}
                        {shouldShow("store_buy_motive") && (
                          <Box>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#667781",
                                fontWeight: 600,
                                fontSize: "10px",
                              }}
                            >
                              {getLabel("store_buy_motive").toUpperCase()}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                color: "#111b21",
                                fontSize: "13px",
                                overflowWrap: "break-word",
                                wordBreak: "break-word",
                              }}
                            >
                              {getDisplayValue("store_buy_motive")}
                            </Typography>
                          </Box>
                        )}
                        {shouldShow("store_status") && (
                          <Box>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#667781",
                                fontWeight: 600,
                                fontSize: "10px",
                              }}
                            >
                              {getLabel("store_status").toUpperCase()}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                color: "#111b21",
                                fontSize: "13px",
                                overflowWrap: "break-word",
                                wordBreak: "break-word",
                              }}
                            >
                              {getDisplayValue("store_status")}
                            </Typography>
                          </Box>
                        )}
                        {shouldShow("new_scheduled_date") && (
                          <Box>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#667781",
                                fontWeight: 600,
                                fontSize: "10px",
                              }}
                            >
                              {getLabel("new_scheduled_date").toUpperCase()}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                color: "#111b21",
                                fontSize: "13px",
                                overflowWrap: "break-word",
                                wordBreak: "break-word",
                              }}
                            >
                              {getDisplayValue(
                                "new_scheduled_date",
                                "NÃO INFORMADO",
                                true,
                              )}
                            </Typography>
                          </Box>
                        )}
                        {shouldShow("new_call_back_date") && (
                          <Box>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#667781",
                                fontWeight: 600,
                                fontSize: "10px",
                              }}
                            >
                              {getLabel("new_call_back_date").toUpperCase()}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                color: "#111b21",
                                fontSize: "13px",
                                overflowWrap: "break-word",
                                wordBreak: "break-word",
                              }}
                            >
                              {getDisplayValue(
                                "new_call_back_date",
                                "NÃO INFORMADO",
                                true,
                              )}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                )}
              </Box>

              {/* LADO DIREITO: Preview da Cópia */}
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  minWidth: 0,
                  "@media (max-width: 600px)": {
                    flex: "0 0 auto",
                  },
                }}
              >
                <Card
                  sx={{
                    border: "1px solid #008069",
                    borderRadius: "8px",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                    display: "flex",
                    flexDirection: "column",
                    flex: "1 1 auto",
                    minHeight: "100%",
                    "@media (max-width: 600px)": {
                      minHeight: "250px",
                      flex: "0 0 auto",
                    },
                  }}
                >
                  <CardContent
                    sx={{
                      p: 1.5,
                      display: "flex",
                      flexDirection: "column",
                      flex: 1,
                      minHeight: 0,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 1.5,
                        "@media (max-width: 600px)": {
                          flexDirection: "column",
                          alignItems: "flex-start",
                          gap: 1,
                          mb: 1,
                        },
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <PreviewOutlinedIcon
                          sx={{ fontSize: "18px", color: "#008069" }}
                        />
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 700,
                            color: "#111b21",
                            fontSize: "14px",
                          }}
                        >
                          Preview da Cópia
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          flexWrap: "wrap",
                        }}
                      >
                        <Button
                          size="small"
                          variant={isCopied ? "contained" : "outlined"}
                          startIcon={
                            isCopied ? <CheckCircleIcon /> : <ContentCopyIcon />
                          }
                          onClick={handleCopy}
                          sx={{
                            textTransform: "none",
                            borderRadius: "6px",
                            backgroundColor: isCopied
                              ? "#2e7d32"
                              : "transparent",
                            borderColor: isCopied ? "#2e7d32" : "#008069",
                            color: isCopied ? "white" : "#008069",
                            fontWeight: 600,
                            fontSize: "12px",
                            px: 1.5,
                            py: 0.5,
                            minWidth: "auto",
                            "&:hover": {
                              backgroundColor: isCopied ? "#1b5e20" : "#008069",
                              color: "white",
                            },
                          }}
                        >
                          {isCopied ? "Copiado!" : "Copiar"}
                        </Button>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        position: "relative",
                        flex: 1,
                        backgroundColor: "#efe7de",
                        backgroundImage: `url('/C:/Users/Allan/.gemini/antigravity/brain/f0be8291-6937-41ba-a263-a1de1967d8bc/whatsapp_doodle_bg_1776925041642.png')`,
                        backgroundSize: "400px",
                        backgroundRepeat: "repeat",
                        borderRadius: "12px",
                        p: 2,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-end",
                        overflow: "hidden",
                        minHeight: "200px",
                        "@media (max-width: 600px)": {
                          p: 1.5,
                          minHeight: "150px",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundColor: "rgba(239, 231, 222, 0.4)",
                          zIndex: 0,
                        }}
                      />

                      <Box
                        sx={{
                          position: "relative",
                          zIndex: 1,
                          backgroundColor: "#dcf8c6",
                          p: "8px 12px 10px 12px",
                          borderRadius: "8px 0px 8px 8px",
                          maxWidth: "92%",
                          boxShadow: "0 1px 0.5px rgba(0,0,0,0.13)",
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.25,
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            top: 0,
                            right: "-8px",
                            width: 0,
                            height: 0,
                            borderTop: "10px solid #dcf8c6",
                            borderRight: "10px solid transparent",
                          },
                        }}
                      >
                        {formattedCopyDisplayLines.map((line) => (
                          <Typography
                            key={line.key}
                            component="div"
                            sx={{
                              color: "#111b21",
                              fontSize: "13.5px",
                              lineHeight: 1.45,
                              overflowWrap: "break-word",
                              wordBreak: "break-word",
                              whiteSpace: "pre-wrap",
                              fontFamily:
                                "Segoe UI, Helvetica Neue, Helvetica, Lucida Grande, Arial, Ubuntu, Cantarell, Fira Sans, sans-serif",
                            }}
                          >
                            {line.label ? (
                              <>
                                <strong>{line.label}:</strong>{" "}
                                {line.value || "\u00A0"}
                              </>
                            ) : (
                              line.value || "\u00A0"
                            )}
                          </Typography>
                        ))}

                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            gap: 0.5,
                            mt: 0.5,
                            alignSelf: "flex-end",
                          }}
                        >
                          <Typography
                            sx={{ fontSize: "11px", color: "rgba(0,0,0,0.45)" }}
                          >
                            {new Date().toLocaleTimeString("pt-BR", {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </Typography>
                          <Box sx={{ display: "flex", color: "#53bdeb" }}>
                            <DoneAllIcon sx={{ fontSize: "16px" }} />
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            </Box>

            {/* Observações se existirem - Ocupa as 2 colunas */}
            {shouldShow("note") && (
              <Card
                sx={{
                  border: "1px solid #008069",
                  borderRadius: "8px",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                  mt: 2,
                  "@media (max-width: 600px)": {
                    mt: 1.5,
                  },
                }}
              >
                <CardContent
                  sx={{ p: 1.5, display: "flex", flexDirection: "column" }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <NotesOutlinedIcon
                      sx={{ fontSize: "18px", color: "#008069" }}
                    />
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 700,
                        color: "#111b21",
                        fontSize: "14px",
                      }}
                    >
                      Observações
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#111b21",
                      lineHeight: 1.5,
                      fontSize: "13px",
                      whiteSpace: "pre-wrap",
                      overflowWrap: "break-word",
                      wordBreak: "break-word",
                    }}
                  >
                    {getDisplayValue("note")}
                  </Typography>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default LeadDetailsModal;
